import { Flex, TextInput, Textarea, DatePicker, Text } from '@applyboard/crystal-ui';
import { AdditionalDetailsType } from '../types';

const minDate = new Date();
const maxDate = new Date(2100, 0, 1);
const minDateForIntake = new Date(2000, 0, 1);

export function AdditionalDetails({
  additionalDetails,
  setAdditionalDetails,
  enableFeeFields,
}: {
  additionalDetails: AdditionalDetailsType;
  setAdditionalDetails: (details: AdditionalDetailsType) => void;
  enableFeeFields?: boolean;
}) {
  const handleInputChange = (field: keyof AdditionalDetailsType, value: string) => {
    setAdditionalDetails({
      ...additionalDetails,
      [field]: value,
    });
  };

  const firstDayBeforeTermStart =
    additionalDetails.termStartDate &&
    additionalDetails.firstDayOfClassOn &&
    new Date(additionalDetails.firstDayOfClassOn) < new Date(additionalDetails.termStartDate);

  const lastDayBeforeFirstDay =
    additionalDetails.firstDayOfClassOn &&
    additionalDetails.lastDayOfClassOn &&
    new Date(additionalDetails.lastDayOfClassOn) < new Date(additionalDetails.firstDayOfClassOn);

  return (
    <Flex direction="column" gap={4}>
      {/* Tuition and Fees Section */}
      {enableFeeFields && (
        <>
          <Text variant="titleS">Tuition and Fees</Text>
          <Flex direction="row" gap={4}>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <TextInput
                label="First Payment Fee"
                placeholder="e.g. $1500"
                onChange={(v) => handleInputChange('firstPaymentFee', v)}
                value={additionalDetails.firstPaymentFee?.toString() ?? ''}
                aria-label="First Payment Fee"
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <DatePicker
                label="First Payment Fee Due"
                onChange={(v) => handleInputChange('firstPaymentDate', v)}
                value={additionalDetails.firstPaymentDate ?? ''}
                maxDate={maxDate.toISOString()}
                minDate={minDate.toISOString()}
                aria-label="First Payment Fee Due"
                granularity="day"
              />
            </Flex.Item>
          </Flex>
          <Flex direction="row" gap={4}>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <TextInput
                label="Second Payment Fee"
                placeholder="e.g. $1500"
                onChange={(v) => handleInputChange('secondPaymentFee', v)}
                value={additionalDetails.secondPaymentFee?.toString() ?? ''}
                aria-label="Second Payment Fee"
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <DatePicker
                label="Second Payment Fee Due"
                onChange={(v) => handleInputChange('secondPaymentDate', v)}
                value={additionalDetails.secondPaymentDate ?? ''}
                maxDate={maxDate.toISOString()}
                minDate={minDate.toISOString()}
                aria-label="Second Payment Fee Due"
                granularity="day"
              />
            </Flex.Item>
          </Flex>
          <Flex direction="row" gap={4}>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <TextInput
                label="Tuition Fee"
                placeholder="e.g. $1500"
                onChange={(v) => handleInputChange('tuitionFee', v)}
                value={additionalDetails.tuitionFee?.toString() ?? ''}
                aria-label="Tuition Fee"
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <TextInput
                label="Ancillary Fee"
                placeholder="e.g. $1500"
                onChange={(v) => handleInputChange('ancillaryFee', v)}
                value={additionalDetails.ancillaryFee?.toString() ?? ''}
                aria-label="Ancillary Fee"
              />
            </Flex.Item>
          </Flex>
        </>
      )}

      {/* Academic Dates Section */}
      <Text variant="titleS">Academic Dates</Text>
      <Flex direction="row" gap={4}>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
          <DatePicker
            label="Intake Start Date"
            onChange={(v) => handleInputChange('termStartDate', v)}
            value={additionalDetails.termStartDate ?? ''}
            maxDate={maxDate.toISOString()}
            minDate={minDateForIntake.toISOString()}
            aria-label="Intake Start Date"
            granularity="day"
          />
        </Flex.Item>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
          <DatePicker
            label="First Day of Class"
            onChange={(v) => handleInputChange('firstDayOfClassOn', v)}
            value={additionalDetails.firstDayOfClassOn ?? ''}
            maxDate={maxDate.toISOString()}
            minDate={minDateForIntake.toISOString()}
            aria-label="First Day of Class"
            granularity="day"
            invalid={Boolean(firstDayBeforeTermStart)}
            helpText={firstDayBeforeTermStart ? 'First Day of Class cannot be before Intake Start Date' : undefined}
          />
        </Flex.Item>
      </Flex>
      <Flex direction="row" gap={4}>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
          <DatePicker
            label="Last Day of Class"
            onChange={(v) => handleInputChange('lastDayOfClassOn', v)}
            value={additionalDetails.lastDayOfClassOn ?? ''}
            maxDate={maxDate.toISOString()}
            minDate={minDateForIntake.toISOString()}
            aria-label="Last Day of Class"
            granularity="day"
            invalid={Boolean(lastDayBeforeFirstDay)}
            helpText={lastDayBeforeFirstDay ? 'Last Day of Class cannot be before First Day of Class' : undefined}
          />
        </Flex.Item>
      </Flex>

      {/* Custom Note Section */}
      <Text variant="titleS">Custom Note</Text>
      <Textarea
        intent="secondary"
        onChange={(v) => handleInputChange('additionalNotes', v)}
        value={additionalDetails.additionalNotes ?? ''}
        helpText="Add any additional information or notes about the offer."
        aria-label="Custom Note"
      />
    </Flex>
  );
}
